// RequireAuth — route guard.
//
// Spec: Epic FE-US-INT-01 §6 Phase 0. Wraps the CrmShell route element so
// every page below it requires an authenticated session. While the auth
// provider is hydrating from localStorage we render a spinner, NOT a
// redirect — otherwise a page reload always bounces the user to /login
// before the persisted token has been read.
//
// 401 handling: this component also installs a global "unauthorized"
// handler at mount. When any api-client receives a 401, the handler
// triggers a `logout()` (clearing the stale token) followed by a navigate
// to /login with a `from` state so we can return the user to where they
// were after re-auth.

import { useEffect, useRef, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useTranslation } from '@fluxora/i18n';
import { Spinner } from '@fluxora/ui';

import {
  setOnUnauthorizedHandler,
  useIsAuthHydrated,
  useIsAuthenticated,
  useLogout,
} from '../../lib/authStore';

import { IdleLogout } from './IdleLogout';

export interface RequireAuthProps {
  children: ReactNode;
  /** Where to send unauthenticated users. Defaults to `/login`. */
  redirectTo?: string;
}

export function RequireAuth({ children, redirectTo = '/login' }: RequireAuthProps): JSX.Element {
  const isHydrated = useIsAuthHydrated();
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  const { t } = useTranslation();

  // Install the global session-ended handler. Cleaned up on unmount.
  useEffect(() => {
    setOnUnauthorizedHandler((reason) => {
      logout();
      navigate(redirectTo, { replace: true, state: { from: location.pathname, reason } });
    });
    return () => setOnUnauthorizedHandler(null);
  }, [logout, navigate, redirectTo, location.pathname]);

  /**
   * The session ending UNDERNEATH us — no failed request involved.
   *
   * The auth provider ends a session the moment its access token expires and
   * cannot be renewed (see the watchdog in `@fluxora/auth`'s ropc provider), and
   * an idle operator staring at a dashboard makes no call for a 401 handler to
   * catch. Without this the `<Navigate>` below still fires, but silently — the
   * operator is dropped on a bare login form with no idea why. The ref records
   * that we WERE authenticated, which is what separates "your session expired"
   * from "you are not signed in".
   */
  const wasAuthenticated = useRef(false);
  useEffect(() => {
    if (isAuthenticated) wasAuthenticated.current = true;
  }, [isAuthenticated]);

  if (!isHydrated) {
    // The Common @fluxora/i18n shim returns the key as-is if the dictionary
    // hasn't registered it yet; consumers register `auth.restoringSession` →
    // 'Restoring session' under their locale during boot. Until then the key
    // itself is shown — never a literal English string here.
    const label = t('auth.restoringSession');
    return (
      <div
        className="flex min-h-[40vh] items-center justify-center"
        role="status"
        aria-label={label}
      >
        <Spinner label={label} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        replace
        state={{
          from: location.pathname,
          ...(wasAuthenticated.current ? { reason: 'expired' as const } : {}),
        }}
      />
    );
  }

  return (
    <>
      <IdleLogout />
      {children}
    </>
  );
}
